import { InfoIcon } from '@chakra-ui/icons';
import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightAddon,
  Spinner,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { useGetFundraisingQuery } from '@blockpulse3/graphql/hooks';
import { formatNumberCurrency } from '@blockpulse3/helpers';
import { ErrorMessage, ErrorQueryCard } from '@blockpulse3/ui/commons';

import { getCrowdfundingFundraisingParameters } from '../../../utils';
import { crowdfundingSchema } from './schema';
import { ICrowdfundingFundraisingLegalSubscriberInformations } from './types';

type Props = {
  defaultValues: ICrowdfundingFundraisingLegalSubscriberInformations;
  onSubmit: (data: ICrowdfundingFundraisingLegalSubscriberInformations) => void;
};

export function CrowdfundingLegalSubscriberInformationsForm({
  defaultValues,
  onSubmit,
}: Props): JSX.Element {
  const i18nSubscriberInformationsForm = useTranslations('SubscriberInformationsForm');
  const i18nLegalSubscriberInformationsForm = useTranslations('LegalSubscriberInformationsForm');

  const { fundraisingId = '' } = useParams();
  const [hardCap, setHardCap] = useState<number>(0);

  const { register, formState, handleSubmit } =
    useForm<ICrowdfundingFundraisingLegalSubscriberInformations>({
      defaultValues,
      resolver: yupResolver(crowdfundingSchema),
      context: { hardCap },
    });

  const { data, loading, error } = useGetFundraisingQuery({
    variables: {
      fundraisingId,
    },
    skip: !fundraisingId,
  });

  const parameters = useMemo(
    () => getCrowdfundingFundraisingParameters(data?.fundraising),
    [data?.fundraising],
  );

  useEffect(() => {
    setHardCap(parameters.hardCap || 0);
  }, [parameters]);

  if (loading) {
    //TODO: remove spinner
    return <Spinner />;
  }

  if (error || !data) {
    return <ErrorQueryCard />;
  }

  const handleFormSubmit: SubmitHandler<ICrowdfundingFundraisingLegalSubscriberInformations> = (
    data,
  ): void => {
    onSubmit(data);
  };

  /*
  const handleFormChange = (e: React.ChangeEvent<HTMLFormElement>): void => {
    if (e.target.id === 'amount') {
      setIsAmountTouched(true);
    }
  };
	*/

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing="8">
        <Stack spacing="4">
          <FormControl isInvalid={!!formState.errors.email}>
            <FormLabel htmlFor="email">{i18nLegalSubscriberInformationsForm('Email')}</FormLabel>
            <Input id="email" type="string" {...register('email')} />
            <ErrorMessage error={formState.errors.email} />
          </FormControl>
          <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
            <FormControl isInvalid={!!formState.errors.name}>
              <FormLabel htmlFor="name">{i18nLegalSubscriberInformationsForm('Name')}</FormLabel>
              <Input id="name" type="string" {...register('name')} />
              <ErrorMessage error={formState.errors.name} />
            </FormControl>
            <FormControl isInvalid={!!formState.errors.identificationNumber}>
              <HStack alignItems="flex-start" spacing="0">
                <FormLabel htmlFor="identificationNumber">
                  {i18nLegalSubscriberInformationsForm('IdentificationNumber')}
                </FormLabel>
                <Tooltip
                  hasArrow
                  label={i18nLegalSubscriberInformationsForm('Tooltip.IdentificationNumber')}
                  placement="top"
                >
                  <Icon as={InfoIcon} color="gray.500" />
                </Tooltip>
              </HStack>
              <Input
                id="identificationNumber"
                type="string"
                {...register('identificationNumber')}
              />
              <ErrorMessage error={formState.errors.identificationNumber} />
            </FormControl>
          </Stack>
          <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
            <FormControl isInvalid={!!formState.errors.minimalAmount}>
              <FormLabel htmlFor="minimalAmount">
                {i18nSubscriberInformationsForm('MinimalAmount')}
              </FormLabel>
              <Input
                id="minimalAmount"
                step="0.01"
                type="number"
                {...register('minimalAmount', { valueAsNumber: true })}
              />
              <Text color="gray.500" fontSize="xs">
                {i18nSubscriberInformationsForm('Default')}{' '}
                {formatNumberCurrency(parameters.minimalAmount || parameters.pricePerShare)}
              </Text>
              <ErrorMessage error={formState.errors.minimalAmount} />
            </FormControl>
            <FormControl isInvalid={!!formState.errors.maximalAmount}>
              <FormLabel htmlFor="maximalAmount">
                {i18nSubscriberInformationsForm('MaximalAmount')}
              </FormLabel>
              <Input
                id="maximalAmount"
                step="0.01"
                type="number"
                {...register('maximalAmount', { valueAsNumber: true })}
              />
              <Text color="gray.500" fontSize="xs">
                {i18nSubscriberInformationsForm('Default')}{' '}
                {formatNumberCurrency(parameters.maximalAmount || parameters.hardCap || 0)}
              </Text>
              <ErrorMessage error={formState.errors.maximalAmount} />
            </FormControl>
          </Stack>
          <FormControl isInvalid={!!formState.errors.admissionFees}>
            <FormLabel htmlFor="admissionFees">
              {i18nLegalSubscriberInformationsForm('EntranceFee')}
            </FormLabel>
            <InputGroup>
              <Input id="admissionFees" step="0.01" type="number" {...register('admissionFees')} />
              <InputRightAddon>%</InputRightAddon>
            </InputGroup>
            <ErrorMessage error={formState.errors.admissionFees} />
          </FormControl>
        </Stack>
        <Button data-cy="validate-subscriber" type="submit" w="full">
          {i18nLegalSubscriberInformationsForm('AddSubscriber')}
        </Button>
      </Stack>
    </form>
  );
}

export type CrowdfundingLegalSubscriberInformationsFormProps = Props;
